/* Trending tropes rail — Feb-2026 (indie-conv batch2 pt3).
 *
 * Renders a horizontal chip row of the top N tropes across the visible
 * catalog with per-trope book counts. Each chip links to the primary
 * genre where that trope lives with `?trope=<name>` so `GenrePage`
 * picks up the filter automatically.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { bookstoreApi } from '@/lib/bookstoreApi';

export default function TopTropesRail() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    bookstoreApi.topTropes(12)
      .then((r) => setRows(r.tropes || []))
      .catch(() => setRows([]));
  }, []);

  if (rows === null) return null;
  if (rows.length === 0) return null;

  return (
    <section
      className="relative z-10 mx-auto max-w-6xl px-6 pt-8 lg:pl-24"
      data-testid="bs-top-tropes-rail"
    >
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.32em]"
               style={{ color: 'hsl(0 40% 30%)' }}>
            <Sparkles className="mr-1 inline h-3 w-3" /> Trending tropes
          </div>
          <div className="mt-0.5 text-xl font-semibold tracking-tight">
            Find your next favourite by vibe
          </div>
        </div>
        <div className="text-[11px] text-muted-foreground">
          Click a trope to see every shelf it lives on.
        </div>
      </div>
      <div
        className="flex flex-wrap items-center gap-2"
        data-testid="bs-top-tropes-chips"
      >
        {rows.map((r) => (
          <Link
            key={r.trope}
            to={r.primary_genre
              ? `/bookstore/g/${r.primary_genre}?trope=${encodeURIComponent(r.trope)}`
              : '/bookstore'}
            data-testid={`bs-top-trope-chip-${r.trope}`}
            className="group inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-medium uppercase tracking-wider transition hover:-translate-y-0.5 hover:shadow-md"
            style={{
              borderColor: 'hsl(0 40% 30% / 0.35)',
              backgroundColor: 'hsl(0 40% 30% / 0.06)',
              color: 'hsl(0 40% 25%)',
            }}
          >
            {r.trope}
            <span className="rounded-full bg-oxblood/15 px-1.5 py-[1px] text-[10px] font-bold text-oxblood group-hover:bg-oxblood group-hover:text-white">
              {r.count}
            </span>
            {typeof r.delta === 'number' && r.delta > 0 && (
              <span
                data-testid={`bs-top-trope-delta-${r.trope}`}
                title={`${r.week_count} opens in the last week (up from ${r.prev_week_count})`}
                className="inline-flex items-center gap-0.5 rounded-full bg-emerald-500/15 px-1.5 py-[1px] text-[10px] font-bold text-emerald-700 dark:text-emerald-400"
              >
                <span aria-hidden="true">▲</span>
                {r.delta} this week
              </span>
            )}
          </Link>
        ))}
      </div>
    </section>
  );
}
